import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AESTHETIC_PRESETS } from "@/lib/themes";
import { ArrowRight, ArrowLeft, Plus, X, Check, Sparkles, Star, Feather, Palette } from "lucide-react";
import { toast, Toaster } from "sonner";

const STEPS = [
  { key: "dream", label: "Your dream", icon: Star },
  { key: "anime", label: "Your worlds", icon: Feather },
  { key: "aesthetic", label: "Your vibe", icon: Palette },
];

export default function Onboarding() {
  const { patchLocalUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  const [dream, setDream] = useState({ full_text: "", north_star_short: "", target_uni: "", target_grades: "" });
  const [anime, setAnime] = useState([]);
  const [aesthetic, setAesthetic] = useState("sakura_pastel");

  const goNext = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const canProceed = () => {
    if (step === 0) return dream.north_star_short.trim().length > 0;
    return true;
  };

  const finish = async () => {
    setBusy(true);
    try {
      const payload = {
        dream: {
          full_text: dream.full_text,
          north_star_short: dream.north_star_short,
          target_uni: dream.target_uni || null,
          target_grades: dream.target_grades || null,
        },
        anime: anime.filter((a) => a.title && a.title.trim()).map((a) => ({
          title: a.title.trim(),
          character: (a.character || "").trim() || null,
          quotes: (a.quotes || []).map((q) => q.trim()).filter(Boolean).slice(0, 3),
        })),
        aesthetic_preset: aesthetic,
      };
      const { data } = await api.post("/onboarding", payload);
      patchLocalUser({ onboarding_completed: true, aesthetic_preset: aesthetic });
      // Apply immediately for a smooth transition
      document.documentElement.setAttribute("data-theme", aesthetic);
      toast.success("Beautiful. Welcome to your Ascend.");
      navigate("/dashboard", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const StepIcon = STEPS[step].icon;

  return (
    <div className="min-h-screen relative px-5 py-10">
      <div className="ascend-atmosphere" />
      <Toaster richColors position="top-center" />

      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 justify-center mb-8 fade-up">
          <div className="w-10 h-10 rounded-full flex items-center justify-center soft-pulse"
               style={{ background: "var(--primary-soft)" }}>
            <Sparkles size={18} strokeWidth={1.5} style={{ color: "var(--primary)" }} />
          </div>
          <div className="text-center">
            <div className="font-display text-2xl" style={{ color: "var(--text)" }}>Ascend</div>
            <div className="font-hand text-sm -mt-1" style={{ color: "var(--accent)" }}>let's set the scene</div>
          </div>
        </div>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-3 mb-8" data-testid="onboarding-progress">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex items-center gap-3">
              <div
                className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-all"
                style={{
                  background: i === step ? "var(--surface)" : "transparent",
                  color: i <= step ? "var(--primary)" : "var(--text-muted)",
                  border: i === step ? "1px solid var(--border)" : "1px solid transparent",
                }}
              >
                {i < step ? <Check size={12} strokeWidth={2} /> : <span className="w-3 text-center">{i + 1}</span>}
                <span className="hidden sm:inline font-medium">{s.label}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className="w-6 h-px" style={{ background: "var(--border)" }} />
              )}
            </div>
          ))}
        </div>

        <div key={step} className="card-soft p-6 sm:p-10 fade-up" data-testid={`onboarding-step-${STEPS[step].key}`}>
          <div className="flex items-center gap-2 mb-4">
            <StepIcon size={16} strokeWidth={1.5} style={{ color: "var(--primary)" }} />
            <span className="eyebrow">step {step + 1} of {STEPS.length}</span>
          </div>

          {step === 0 && <StepDream dream={dream} setDream={setDream} />}
          {step === 1 && <StepAnime anime={anime} setAnime={setAnime} />}
          {step === 2 && <StepAesthetic aesthetic={aesthetic} setAesthetic={setAesthetic} />}

          <div className="mt-10 flex items-center justify-between gap-3">
            {step > 0 ? (
              <button onClick={goBack} className="btn-ghost flex items-center gap-2 text-sm" data-testid="onboarding-back-btn">
                <ArrowLeft size={16} strokeWidth={1.5} /> Back
              </button>
            ) : <div />}

            {step < STEPS.length - 1 ? (
              <button
                onClick={goNext}
                disabled={!canProceed()}
                className="btn-primary flex items-center gap-2 disabled:opacity-50"
                data-testid="onboarding-next-btn"
              >
                Continue <ArrowRight size={16} strokeWidth={1.75} />
              </button>
            ) : (
              <button
                onClick={finish}
                disabled={busy}
                className="btn-primary flex items-center gap-2 disabled:opacity-60"
                data-testid="onboarding-finish-btn"
              >
                {busy ? "Setting up…" : "Enter Ascend"} <ArrowRight size={16} strokeWidth={1.75} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StepDream({ dream, setDream }) {
  return (
    <div>
      <h2 className="font-display text-3xl sm:text-4xl leading-tight" style={{ color: "var(--text)" }}>
        Define your dream.
      </h2>
      <p className="text-muted mt-2 mb-6">
        Write it fully. Then give it a shorter version — the one line that'll sit on your dashboard when the days get hard.
      </p>

      <div className="space-y-5">
        <div>
          <label className="text-xs uppercase tracking-wider text-muted mb-1 block">The full version</label>
          <textarea
            className="input-soft"
            placeholder="I want to study psychology and neuroscience because…"
            value={dream.full_text}
            onChange={(e) => setDream({ ...dream, full_text: e.target.value })}
            data-testid="dream-full-input"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-muted mb-1 block">Your north star (one line)</label>
          <input
            className="input-soft"
            placeholder="e.g. Psychology & Neuroscience — because how we feel is who we become."
            value={dream.north_star_short}
            onChange={(e) => setDream({ ...dream, north_star_short: e.target.value })}
            maxLength={200}
            data-testid="dream-northstar-input"
          />
          <div className="text-xs text-muted mt-1">This shows on your dashboard.</div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-muted mb-1 block">Target university (optional)</label>
            <input
              className="input-soft"
              placeholder="UCL"
              value={dream.target_uni}
              onChange={(e) => setDream({ ...dream, target_uni: e.target.value })}
              data-testid="dream-uni-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted mb-1 block">Target grades (optional)</label>
            <input
              className="input-soft"
              placeholder="A*A*A"
              value={dream.target_grades}
              onChange={(e) => setDream({ ...dream, target_grades: e.target.value })}
              data-testid="dream-grades-input"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function StepAnime({ anime, setAnime }) {
  const canAdd = anime.length < 5;
  const add = () => canAdd && setAnime([...anime, { title: "", character: "", quotes: [""] }]);
  const remove = (idx) => setAnime(anime.filter((_, i) => i !== idx));
  const update = (idx, patch) => setAnime(anime.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  const setQuote = (idx, qi, v) => {
    const quotes = [...(anime[idx].quotes || [])];
    quotes[qi] = v;
    update(idx, { quotes });
  };
  const addQuote = (idx) => {
    const quotes = [...(anime[idx].quotes || [])];
    if (quotes.length < 3) update(idx, { quotes: [...quotes, ""] });
  };
  const removeQuote = (idx, qi) => update(idx, { quotes: anime[idx].quotes.filter((_, i) => i !== qi) });

  // Library lookup: fuzzy match by title (case-insensitive substring)
  const [library, setLibrary] = React.useState([]);
  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/anime-library");
        setLibrary(data.items || []);
      } catch (e) { /* silent — suggestions are optional */ }
    })();
  }, []);

  const matchLibraryFor = (title) => {
    const t = (title || "").trim().toLowerCase();
    if (t.length < 2) return null;
    // Exact-ish match first
    const exact = library.find((a) => a.anime_title.toLowerCase() === t);
    if (exact) return exact;
    // Substring — take the first anime whose lowercased title includes the input
    return library.find((a) => a.anime_title.toLowerCase().includes(t)) || null;
  };

  const toggleSuggestion = (idx, quoteText) => {
    const a = anime[idx];
    const quotes = [...(a.quotes || []).filter((q) => q && q.trim() !== "")];
    const existsIdx = quotes.findIndex((q) => q.trim() === quoteText.trim());
    if (existsIdx >= 0) {
      quotes.splice(existsIdx, 1);
      update(idx, { quotes });
    } else if (quotes.length < 3) {
      quotes.push(quoteText);
      update(idx, { quotes });
    }
  };

  return (
    <div>
      <h2 className="font-display text-3xl sm:text-4xl leading-tight" style={{ color: "var(--text)" }}>
        Your favourite worlds.
      </h2>
      <p className="text-muted mt-2 mb-6">
        Up to five anime. Add a favourite character if you want, and any quotes you love (type them yourself — we won't ship anyone else's words).
      </p>

      <div className="space-y-4">
        {anime.length === 0 && (
          <div className="card-soft-2 p-6 text-center text-muted font-hand text-lg">
            Nothing here yet — this step is optional.
          </div>
        )}

        {anime.map((a, idx) => {
          const match = matchLibraryFor(a.title);
          const currentQuotes = new Set((a.quotes || []).filter(Boolean).map((q) => q.trim()));
          return (
          <div key={idx} className="card-soft-2 p-5" data-testid={`anime-card-${idx}`}>
            <div className="flex items-start gap-3">
              <div className="flex-1 space-y-3">
                <input
                  className="input-soft"
                  placeholder="Anime title (e.g. Spirited Away)"
                  value={a.title}
                  onChange={(e) => update(idx, { title: e.target.value })}
                  data-testid={`anime-title-${idx}`}
                />
                {match && (
                  <div
                    className="rounded-xl px-3 py-2.5"
                    style={{ background: "color-mix(in oklab, var(--primary-soft) 30%, var(--surface))", border: "1px dashed var(--border)" }}
                    data-testid={`anime-suggestions-${idx}`}
                  >
                    <div className="text-[11px] text-muted italic mb-2">
                      We have {match.quotes.length} quotes from <span style={{ color: "var(--text)" }}>{match.anime_title}</span> — tap to add any (max 3):
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {match.quotes.slice(0, 8).map((quote) => {
                        const active = currentQuotes.has(quote.quote_text.trim());
                        return (
                          <button
                            key={quote.id}
                            type="button"
                            onClick={() => toggleSuggestion(idx, quote.quote_text)}
                            className="text-[11px] px-2.5 py-1.5 rounded-full text-left leading-snug transition-colors max-w-full"
                            style={{
                              background: active ? "var(--primary)" : "var(--surface)",
                              color: active ? "#fff" : "var(--text)",
                              border: `1px solid ${active ? "var(--primary)" : "var(--border)"}`,
                            }}
                            title={quote.quote_text}
                            data-testid={`anime-suggestion-${idx}-${quote.id}`}
                          >
                            <span className="italic">"{quote.quote_text.length > 60 ? quote.quote_text.slice(0, 60) + "…" : quote.quote_text}"</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <input
                  className="input-soft"
                  placeholder="Favourite character (optional)"
                  value={a.character || ""}
                  onChange={(e) => update(idx, { character: e.target.value })}
                  data-testid={`anime-character-${idx}`}
                />
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wider text-muted">Quotes (optional, up to 3)</div>
                  {(a.quotes || []).map((q, qi) => (
                    <div key={qi} className="flex items-center gap-2">
                      <input
                        className="input-soft"
                        placeholder="Type a quote you love…"
                        value={q}
                        onChange={(e) => setQuote(idx, qi, e.target.value)}
                        data-testid={`anime-quote-${idx}-${qi}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeQuote(idx, qi)}
                        className="p-2 rounded-full hover:bg-[var(--surface-2)]"
                        aria-label="Remove quote"
                      >
                        <X size={14} strokeWidth={1.5} />
                      </button>
                    </div>
                  ))}
                  {(a.quotes || []).length < 3 && (
                    <button
                      type="button"
                      onClick={() => addQuote(idx)}
                      className="text-sm flex items-center gap-1 mt-1"
                      style={{ color: "var(--primary)" }}
                      data-testid={`anime-add-quote-${idx}`}
                    >
                      <Plus size={14} strokeWidth={1.75} /> Add a quote
                    </button>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => remove(idx)}
                className="btn-ghost text-xs"
                data-testid={`anime-remove-${idx}`}
              >
                Remove
              </button>
            </div>
          </div>
          );
        })}

        {canAdd && (
          <button
            type="button"
            onClick={add}
            className="btn-ghost w-full flex items-center justify-center gap-2 text-sm"
            data-testid="anime-add-btn"
          >
            <Plus size={16} strokeWidth={1.75} /> Add an anime ({anime.length}/5)
          </button>
        )}
      </div>
    </div>
  );
}

function StepAesthetic({ aesthetic, setAesthetic }) {
  return (
    <div>
      <h2 className="font-display text-3xl sm:text-4xl leading-tight" style={{ color: "var(--text)" }}>
        Pick a starting vibe.
      </h2>
      <p className="text-muted mt-2 mb-6">
        These change how the app feels. You can switch later, on Subjects.
      </p>

      <div className="grid sm:grid-cols-2 gap-4">
        {AESTHETIC_PRESETS.map((p) => (
          <button
            type="button"
            key={p.id}
            onClick={() => {
              setAesthetic(p.id);
              document.documentElement.setAttribute("data-theme", p.id);
            }}
            data-selected={aesthetic === p.id}
            className="preset-chip text-left"
            data-testid={`preset-${p.id}`}
            style={{ background: p.gradient, color: p.text }}
          >
            <div className="font-display text-2xl leading-tight">{p.name}</div>
            <div className="text-xs mt-1 opacity-80">{p.tagline}</div>
            <div className="flex gap-1.5 mt-4">
              {p.swatches.map((c) => (
                <span key={c} className="w-5 h-5 rounded-full border border-white/40" style={{ background: c }} />
              ))}
            </div>
            {aesthetic === p.id && (
              <span
                className="absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center"
                style={{ background: p.text, color: "#fff" }}
              >
                <Check size={14} strokeWidth={2} />
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
